/**
 * A custom React hook that manages scroll behavior for a Tiptap editor.
 * This hook ensures that the editor content remains visible and properly positioned
 * during user interaction, particularly when typing or navigating through content.
 */

import { useEffect, useRef, useCallback } from 'react';
import { Editor } from '@tiptap/react';

/**
 * Configuration options for the scroll adjustment hook
 * @interface UseTiptapScrollAdjustmentOptions
 * @property {Editor | null} editor - The Tiptap editor instance to manage
 * @property {number} [offset=100] - The minimum distance (in pixels) to maintain between the editor and viewport bottom
 * @property {ScrollBehavior} [behavior='smooth'] - The scroll behavior to use ('auto' or 'smooth')
 * @property {number} [debounceDelay=100] - Delay in milliseconds before applying scroll adjustments
 * @property {boolean} [scrollToHiddenAnchor=false] - Whether to scroll to a hidden anchor element instead of the editor
 */
interface UseTiptapScrollAdjustmentOptions {
  editor: Editor | null;
  offset?: number;
  behavior?: ScrollBehavior;
  debounceDelay?: number;
  scrollToHiddenAnchor?: boolean;
}

/**
 * Custom hook that manages scroll behavior for a Tiptap editor
 * @param {UseTiptapScrollAdjustmentOptions} options - Configuration options for the scroll behavior
 */
const useScrollHiddenTiptap = (options: UseTiptapScrollAdjustmentOptions) => {
  // Destructure and set default values for options
  const {
    editor,
    offset = 100,
    behavior = 'smooth',
    debounceDelay = 100,
    scrollToHiddenAnchor = false,
  } = options;

  // Refs to track state across renders
  const timeoutRef = useRef<NodeJS.Timeout>(); // Stores the debounce timeout
  const isUserScrolling = useRef(false); // Tracks if user is manually scrolling
  const isInputting = useRef(false); // Tracks if user is currently typing

  /**
   * Scrolls an element into view with a specified offset from the viewport bottom
   * @param {HTMLElement} element - The element to scroll into view
   * @param {boolean} force - Whether to force scroll regardless of user scrolling state
   */
  const scrollIntoViewWithOffset = useCallback(
    (element: HTMLElement, force = false) => {
      // Don't scroll if user is manually scrolling or typing (unless forced)
      if (!force && isUserScrolling.current) return;

      // Calculate element position and viewport dimensions
      const rect = element.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const isObscured = rect.bottom > viewportHeight - offset;
      const isElementLargerThanViewport = rect.height > viewportHeight;

      if (isObscured) {
        // Choose scroll behavior based on element size
        let decideBlockType: ScrollIntoViewOptions['block'] = isElementLargerThanViewport ? 'end' : 'center';

        // Handle scrolling to either hidden anchor or the editor element
        const hiddenAnchor = document.getElementById('hidden-anchor');
        if (scrollToHiddenAnchor && hiddenAnchor) {
          hiddenAnchor.scrollIntoView({ behavior, block: decideBlockType });
        } else {
          element.scrollIntoView({ behavior, block: decideBlockType });
        }
      }
    },
    [scrollToHiddenAnchor, offset, behavior]
  );

  /**
   * Debounced version of scrollIntoViewWithOffset to prevent excessive scrolling
   * @param {HTMLElement} element - The element to scroll into view
   */
  const debouncedScroll = useCallback(
    (element: HTMLElement) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        isInputting.current = false;
      }

      timeoutRef.current = setTimeout(() => {
        scrollIntoViewWithOffset(element, true);
      }, debounceDelay);
    },
    [debounceDelay, scrollIntoViewWithOffset]
  );

  useEffect(() => {
    if (!editor?.view?.dom) return;

    const editorElement = editor.view.dom;

    /**
     * Handles window scroll events to detect user-initiated scrolling
     */
    const handleScroll = () => {
      if (!isInputting.current) {
        isUserScrolling.current = true;
      }
    };

    /**
     * Detects user input and navigation to manage scroll behavior
     * @param {KeyboardEvent} event - The keyboard event
     */
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isInputting.current) {
        const isNavigationKey = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key);
        // True if the key is a single character, backspace, or delete
        const isTypingKey = event.key.length === 1 || event.key === 'Backspace' || event.key === 'Delete';

        if (isNavigationKey || isTypingKey) {
          isInputting.current = true;
        }
      }
    };

    /**
     * Handles editor focus events to ensure visibility
     */
    const handleFocus = () => {
      isInputting.current = true;
      scrollIntoViewWithOffset(editorElement, true);
    };

    const handleCreate = () => {
      scrollIntoViewWithOffset(editorElement, true);
    };

    // Set up intersection observer to detect when editor goes out of view
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting && editor.isFocused && isInputting.current) {
            scrollIntoViewWithOffset(editorElement, true);
          }
        });
      },
      {
        root: null,
        rootMargin: `-${offset}px 0px 0px 0px`, // Creates a margin at the bottom of the viewport
        threshold: 1.0, // Only trigger when element is fully visible/invisible
      }
    );

    // Start observing the editor element
    observer.observe(editorElement);

    // Set up event listeners
    window.addEventListener('scroll', handleScroll, { passive: true });
    editorElement.addEventListener('keydown', handleKeyDown);
    editor.on('focus', handleFocus);
    editor.on('create', handleCreate);

    // Cleanup function to remove event listeners and observers
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
      editorElement.removeEventListener('keydown', handleKeyDown);
      editor.off('focus', handleFocus);
      editor.off('create', handleCreate);
    };
  }, [editor, offset, debouncedScroll, scrollIntoViewWithOffset]);
};

export default useScrollHiddenTiptap;
